import React, { useState } from 'react';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Role } from '../../api/lib/api/user';
import { useUser, useSelect } from '../../api';
import PageTitle from '../common/BrowserPageTitle';
import ProfileHeader from '../../containers/ProfileHeader';
import ProfileTabs from './Tabs';
import About from './About';

import renderMetaTags from '../../utils/renderMetaTags';
import { useUserStatus } from '../../hooks/useUserStatus';
import { useIsDesktop } from '../../style/constants';

import RequestFeedbackDialog from '../Modals/RequestFeedbackDialog';
import NoVCoinsDialog from '../Modals/NoVCoinsDialog';

import ProfileLabel from './ProfileLabel';
import NotFound from '../../pages/_error';

export interface StateProps {
  currentId?: number;
  role?: Role | null;
  userHasTracks?: boolean;
}

export interface DispatchProps {}

export interface OwnProps {
  profileId?: number;
}

type Props = StateProps & DispatchProps & OwnProps;

const ProfilePage = ({
  id,
  currentId,
  profileId,
  userHasTracks,
}: Props & { id: number }) => {
  const { t } = useTranslation('profile');
  const { data: user, isLoading } = useUser(id);
  const current = Boolean(user && currentId === user.id);

  const isDesktop = useIsDesktop();

  const [showRequestFeedbackModal, setShowRequestFeedbackModal] = useState(
    false,
  );
  const [showNoVCoinsDialog, setShowNoVCoinsDialog] = useState(false);

  useUserStatus(id);

  const router = useRouter();
  const loggedIn = useSelect(
    ({ authentication: { isLoggedIn } }) => isLoggedIn,
  );

  const showFeedbackModalOrRedirect = () => {
    if (loggedIn) {
      setShowRequestFeedbackModal(true);
    } else {
      router.push({
        pathname: '/login',
        query: {
          redirect: router.asPath,
        },
      });
    }
  };

  return (
    <StyledContainer fluid>
      <PageTitle description={user && user.about} noGoogleIndex>
        {` ${t('profile:profileOnVirpp')}${user && user.display_name}`}
      </PageTitle>
      {user &&
        renderMetaTags({
          'og:type': 'profile',
          'og:url': `https://virpp.com/profile/${user.id}`,
          'og:title': user.display_name,
          'og:image': user.share.fb_share_1200x630,
          'og:description': user.about,
          'og:image:alt': `${t('profile:profileImageFor')} ${
            user.display_name
          } ${t('profile:onVirpp')}`,
          'twitter:title': user.display_name,
          'twitter:image': user.share.share_1598x1598,
          'twitter:description': user.about,
        })}
      <ProfileHeader
        isReady={!isLoading}
        user={user}
        notCurrent={!current}
        profileId={profileId}
        showFeedbackModal={showFeedbackModalOrRedirect}
      />
      <Row style={{ marginTop: 28 }}>
        <Col lg={3} md={12}>
          <About
            isReady={!isLoading}
            user={user}
            showFeedbackModal={showFeedbackModalOrRedirect}
            current={current}
          />
        </Col>
        <Col lg={9} md={12} noGutter={!isDesktop && user?.role !== 'label'}>
          {user?.role === 'label' ? (
            <ProfileLabel user={user} />
          ) : (
            <ProfileTabs
              profileId={id}
              currentUser={current}
              role={user && user.role}
              userHasTracks={userHasTracks}
            />
          )}
        </Col>
      </Row>
      {user && (
        <RequestFeedbackDialog
          userId={user.id}
          open={showRequestFeedbackModal}
          showNoVCoinsDialog={() => setShowNoVCoinsDialog(true)}
          onClose={() => setShowRequestFeedbackModal(false)}
        />
      )}
      {user && (
        <NoVCoinsDialog
          open={showNoVCoinsDialog}
          onClose={() => setShowNoVCoinsDialog(false)}
        />
      )}
    </StyledContainer>
  );
};

// added key to ProfileWithTabs to make them properly rerender on transitioning between profiles
const WrappedProfile = (props: Props) => {
  const { profileId, currentId } = props;
  const id = profileId || currentId;

  if (!id) {
    return <NotFound />;
  }

  return <ProfilePage {...props} id={id} />;
};

export default WrappedProfile;

const StyledContainer = styled(Container)`
  max-width: 1360px;
  width: 100%;
  margin: 0 auto;
`;
